import { useDeferred } from '#src/clientState/hooks/useDeferred';
import {
  ADD_CONTACT_DETAILS_TO_UNIVERSAL_SHOPPING_BASKET,
  ADD_PRODUCT_TO_UNIVERSAL_SHOPPING_BASKET,
  REMOVE_PRODUCT_FROM_UNIVERSAL_SHOPPING_BASKET,
} from '#src/clientState/providers/ShoppingBasketProvider/operations/ShoppingBasketProvider.mutations';
import {
  UniversalShoppingBasketProductOperation,
  UseAddContactDetailsToUniversalShoppingBasketType,
  UseAddProductToUniversalShoppingBasketType,
  UseRemoveProductFromUniversalShoppingBasketType,
} from '#src/clientState/providers/ShoppingBasketProvider/types';
import {
  AddContactDetailsToUniversalShoppingBasketRequest,
  Sb_AddContactDetailsToUniversalShoppingBasketMutation,
  Sb_AddContactDetailsToUniversalShoppingBasketMutationVariables,
  Sb_AddProductToUniversalShoppingBasketMutation,
  Sb_AddProductToUniversalShoppingBasketMutationVariables,
  Sb_RemoveProductFromUniversalShoppingBasketMutation,
  Sb_RemoveProductFromUniversalShoppingBasketMutationVariables,
} from '#src/generated/graphql';
import {
  UseMutationResult,
  useMutation,
} from '@edreamsodigeo/frontend-api-client';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { useEffect, useState } from 'react';

const useShoppingBasketMutation = (mutation) => {
  const { resolve, reset, reject, promise } = useDeferred();
  const [shouldReject, setShouldReject] = useState(false);

  const productToShoppingBasketResponse: UseMutationResult<
    | Sb_AddProductToUniversalShoppingBasketMutation
    | Sb_RemoveProductFromUniversalShoppingBasketMutation,
    | Sb_AddProductToUniversalShoppingBasketMutationVariables
    | Sb_RemoveProductFromUniversalShoppingBasketMutationVariables
  > = useMutation(mutation, {
    onSuccess: ({ data }) => {
      resolve(data);
      reset();
      setShouldReject(false);
    },
  });

  useFrontendApiBlockingError(productToShoppingBasketResponse);

  const {
    data: result,
    errors,
    mutate,
    loading,
  } = productToShoppingBasketResponse;

  useEffect(() => {
    if (errors?.length && shouldReject) {
      reject(errors);
      reset();
      setShouldReject(false);
    }
  }, [errors, reject, reset, shouldReject]);

  const mutateOperation = ({
    shoppingBasketId,
    productId,
    productType,
  }: UniversalShoppingBasketProductOperation) => {
    setShouldReject(true);
    mutate({
      request: {
        shoppingBasketId,
        product: {
          id: productId,
          type: productType,
        },
      },
    });
    return promise;
  };

  return {
    result,
    mutateOperation,
    loading,
    errors,
  };
};

export const useAddProductToShoppingBasket =
  (): UseAddProductToUniversalShoppingBasketType => {
    const {
      result,
      mutateOperation: addProductToShoppingBasket,
      loading,
      errors,
    } = useShoppingBasketMutation(ADD_PRODUCT_TO_UNIVERSAL_SHOPPING_BASKET);

    return {
      result,
      addProductToShoppingBasket,
      loading,
      errors,
    };
  };

export const useAddContactDetailsToShoppingBasket =
  (): UseAddContactDetailsToUniversalShoppingBasketType => {
    const { resolve, reset, reject, promise } = useDeferred();
    const [shouldReject, setShouldReject] = useState(false);

    const addContactDetailsToShoppingBasketResponse: UseMutationResult<
      Sb_AddContactDetailsToUniversalShoppingBasketMutation,
      Sb_AddContactDetailsToUniversalShoppingBasketMutationVariables
    > = useMutation(ADD_CONTACT_DETAILS_TO_UNIVERSAL_SHOPPING_BASKET, {
      onSuccess: ({ data }) => {
        resolve(data);
        reset();
        setShouldReject(false);
      },
    });

    useFrontendApiBlockingError(addContactDetailsToShoppingBasketResponse);

    const {
      data: result,
      errors,
      mutate,
      loading,
    } = addContactDetailsToShoppingBasketResponse;

    useEffect(() => {
      if (errors?.length && shouldReject) {
        reject(errors);
        reset();
        setShouldReject(false);
      }
    }, [errors, reject, reset, shouldReject]);

    const addContactDetailsToShoppingBasket = ({
      shoppingBasketId,
      contactDetails,
    }: AddContactDetailsToUniversalShoppingBasketRequest) => {
      setShouldReject(true);
      mutate({
        request: {
          shoppingBasketId,
          contactDetails,
        },
      });

      return promise;
    };

    return {
      result,
      addContactDetailsToShoppingBasket,
      loading,
      errors,
    };
  };

export const useRemoveProductFromShoppingBasket =
  (): UseRemoveProductFromUniversalShoppingBasketType => {
    const {
      result,
      mutateOperation: removeProductFromShoppingBasket,
      loading,
      errors,
    } = useShoppingBasketMutation(
      REMOVE_PRODUCT_FROM_UNIVERSAL_SHOPPING_BASKET
    );

    return {
      result,
      removeProductFromShoppingBasket,
      errors,
      loading,
    };
  };
